import PropTypes from 'prop-types';
import React from 'react';
import stylePropType from 'react-style-proptype';
import { twMerge } from 'tailwind-merge';

import AvomaButton from 'components/Common/AvomaButton';
import AvomaModal from 'components/Common/AvomaModal';

import L10n from 'helpers/L10n';

import Tokens from 'styles/tokens';

import Spinner from './Spinner';

const ConfirmModal = ({
  title,
  text,
  confirmText,
  cancelText,
  isOpen,
  onConfirm,
  onCancel,
  positive,
  loading,
  loadingLabel,
  confirmDisabled,
  width,
  hideCancel,
  hideConfirm,
  additionalActions,
  confirmTooltip,
  containerStyles,
  contentStyles,
  additionalText = '',
  confirmLoading = false,
  childrenClassName = '',
  dataTestId = ''
}) => (
  <AvomaModal
    isOpen={isOpen}
    onCancel={onCancel}
    title={title}
    containerStyles={{
      ...styles.containerStyles,
      ...(width && { maxWidth: `${width}px` }),
      ...containerStyles
    }}
    contentStyles={{ ...contentStyles }}
    dataTestId={dataTestId}
  >
    <div className={twMerge('mb-8 whitespace-pre-wrap', childrenClassName)}>
      {loading ? (
        <section style={styles.loader}>
          <Spinner />
          <br />
          {loadingLabel}
        </section>
      ) : (
        <React.Fragment>
          <div>{text}</div>
          {additionalText !== '' && (
            <p className='mt-2 text-base'>{additionalText}</p>
          )}
        </React.Fragment>
      )}
    </div>
    <div style={styles.modalFooter}>
      {additionalActions}
      {!hideCancel && (
        <AvomaButton onClick={onCancel} label={cancelText} black small />
      )}
      {!hideConfirm && (
        <AvomaButton
          onClick={onConfirm}
          label={confirmText}
          className='ml-4'
          destructive={!positive}
          disabled={loading || confirmDisabled}
          tooltip={confirmTooltip}
          tooltipPosition='bottom-left'
          small
          loading={confirmLoading}
        />
      )}
    </div>
  </AvomaModal>
);

const styles = {
  containerStyles: {
    width: '550px',
    maxWidth: '550px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  loader: {
    ...Tokens.type.bodyS,
    flex: '1',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    color: Tokens.colors.silver
  },
  disabledButton: {
    cursor: 'not-allowed',
    opacity: '0.4'
  }
};

ConfirmModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  text: PropTypes.node,
  confirmText: PropTypes.string,
  positive: PropTypes.bool,
  loading: PropTypes.bool,
  loadingLabel: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  width: PropTypes.string,
  cancelText: PropTypes.string,
  hideCancel: PropTypes.bool,
  hideConfirm: PropTypes.bool,
  additionalActions: PropTypes.node,
  confirmTooltip: PropTypes.string,
  containerStyles: stylePropType,
  contentStyles: stylePropType,
  additionalText: PropTypes.string,
  confirmLoading: PropTypes.bool,
  childrenClassName: PropTypes.string,
  dataTestId: PropTypes.string
};

ConfirmModal.defaultProps = {
  title: '',
  text: '',
  cancelText: L10n.general.cancel,
  confirmText: L10n.general.confirm,
  positive: false,
  loading: false,
  confirmDisabled: false,
  hideCancel: false,
  hideConfirm: false
};

export default ConfirmModal;
